import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  createCampaign,
  listPhonebooks,
  listTemplates,
  uploadWhatsAppMedia,
  getWhatsAppErrorMessage,
  unwrapWhatsAppList,
} from "../../services/whatsappApi";
import TemplatePickerCard from "../../components/whatsapp/templates/TemplatePickerCard";
import {
  CAMPAIGN_WIZARD_STEPS,
  CAMPAIGN_TEMPLATE_STATUS_OPTIONS,
  buildEmptyVariableMap,
  extractCampaignBodyVariableIndexes,
  extractCampaignHeaderVariableIndexes,
  getCampaignTemplateBodyText,
  getCampaignTemplateCategory,
  getCampaignTemplateHeaderFormat,
  getCampaignTemplateHeaderText,
  getCampaignTemplateLanguage,
  getCampaignTemplateName,
  getCampaignTemplateStatus,
  mediaTypeFromHeaderFormat,
} from "../../utils/whatsappCampaign";
import {
  WHATSAPP_MEDIA_ACCEPT,
  getWhatsAppMediaTypeLabel,
  validateWhatsAppMediaFile,
} from "../../utils/whatsappMedia";
import "./whatsapp.css";
import "./whatsapp-campaign.css";
import WhatsAppPageShell from "./WhatsAppPageShell";

const TIMEZONE = "Asia/Kolkata";
const TEMPLATE_PAGE_SIZE = 10;

function reportError(err, fallback) {
  const msg = getWhatsAppErrorMessage(err, fallback);
  toast.error(msg);
  return msg;
}

export default function WhatsAppSendCampaign() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [phonebooks, setPhonebooks] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingPhonebooks, setLoadingPhonebooks] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const phonebooksLoadedRef = useRef(false);
  const [statusFilter, setStatusFilter] = useState("APPROVED");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [title, setTitle] = useState("");
  const [phonebookId, setPhonebookId] = useState("");
  const [sendImmediately, setSendImmediately] = useState(true);
  const [scheduleAt, setScheduleAt] = useState("");
  const [variableValues, setVariableValues] = useState({});
  const [headerTextVariable, setHeaderTextVariable] = useState("");
  const [headerMediaFile, setHeaderMediaFile] = useState(null);
  const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState("");
  const headerMediaInputRef = useRef(null);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    setTemplatesError("");
    try {
      const res = await listTemplates({
        status: statusFilter || undefined,
        limit: TEMPLATE_PAGE_SIZE,
        page,
        search: searchQuery,
      });
      const data = unwrapWhatsAppList(res);
      setTemplates(data);
      const fullPage = data.length === TEMPLATE_PAGE_SIZE;
      setHasNextPage(fullPage);
      setTotalPages(fullPage ? page + 1 : page);
    } catch (err) {
      const msg = reportError(err, "Failed to load templates");
      setTemplatesError(msg);
      setTemplates([]);
      setHasNextPage(false);
      setTotalPages(1);
    } finally {
      setLoadingTemplates(false);
    }
  }, [statusFilter, page, searchQuery]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchPhonebooks = useCallback(async ({ force = false } = {}) => {
    if (!force && phonebooksLoadedRef.current) return;
    setLoadingPhonebooks(true);
    try {
      const res = await listPhonebooks();
      setPhonebooks(unwrapWhatsAppList(res));
      phonebooksLoadedRef.current = true;
    } catch (err) {
      reportError(err, "Failed to load phonebooks");
      phonebooksLoadedRef.current = false;
    } finally {
      setLoadingPhonebooks(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
    setPage(1);
  };

  const variableIndexes = useMemo(() => {
    if (!selectedTemplate) return [];
    return extractCampaignBodyVariableIndexes(selectedTemplate);
  }, [selectedTemplate]);

  const headerVarIndexes = useMemo(() => {
    if (!selectedTemplate) return [];
    return extractCampaignHeaderVariableIndexes(selectedTemplate);
  }, [selectedTemplate]);

  const headerFormat = selectedTemplate
    ? getCampaignTemplateHeaderFormat(selectedTemplate)
    : null;
  const headerMediaType = mediaTypeFromHeaderFormat(headerFormat);
  const needsHeaderMedia = Boolean(headerMediaType);
  const needsHeaderTextVar = headerVarIndexes.length > 0;

  const headerText = selectedTemplate
    ? getCampaignTemplateHeaderText(selectedTemplate)
    : "";
  const bodyText = selectedTemplate
    ? getCampaignTemplateBodyText(selectedTemplate)
    : "";

  const clearHeaderMedia = useCallback(() => {
    setHeaderMediaFile(null);
    setHeaderMediaPreviewUrl((prev) => {
      if (prev && prev.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return "";
    });
    if (headerMediaInputRef.current) {
      headerMediaInputRef.current.value = "";
    }
  }, []);

  useEffect(() => {
    return () => {
      if (headerMediaPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(headerMediaPreviewUrl);
      }
    };
  }, [headerMediaPreviewUrl]);

  const canSend =
    title.trim().length > 0 &&
    phonebookId &&
    selectedTemplate &&
    !submitting &&
    !loadingPhonebooks &&
    (!needsHeaderMedia || Boolean(headerMediaFile)) &&
    (!needsHeaderTextVar || Boolean(String(headerTextVariable || "").trim()));

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setVariableValues(
      buildEmptyVariableMap(extractCampaignBodyVariableIndexes(template)),
    );
    setHeaderTextVariable("");
    clearHeaderMedia();
    setFormError("");
  };

  const useTemplate = (template) => {
    selectTemplate(template);
    setStep(1);
    void fetchPhonebooks();
  };

  const goBack = () => {
    setFormError("");
    setStep(0);
  };

  const handleHeaderMediaChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      clearHeaderMedia();
      return;
    }
    const type = headerMediaType || "image";
    const validation = validateWhatsAppMediaFile(file, type);
    if (!validation.ok) {
      toast.error(validation.error);
      if (headerMediaInputRef.current) {
        headerMediaInputRef.current.value = "";
      }
      return;
    }
    setHeaderMediaPreviewUrl((prev) => {
      if (prev && prev.startsWith("blob:")) {
        URL.revokeObjectURL(prev);
      }
      return URL.createObjectURL(file);
    });
    setHeaderMediaFile(file);
    setFormError("");
  };

  const resetConfigureForm = () => {
    setSelectedTemplate(null);
    setTitle("");
    setPhonebookId("");
    setSendImmediately(true);
    setScheduleAt("");
    setVariableValues({});
    setHeaderTextVariable("");
    clearHeaderMedia();
  };

  const handleSubmit = async () => {
    setFormError("");
    if (!selectedTemplate) {
      setFormError("Select a template");
      return;
    }
    if (!title.trim()) {
      setFormError("Campaign title is required");
      return;
    }
    if (!phonebookId) {
      setFormError("Select a phonebook");
      return;
    }
    if (!sendImmediately && !scheduleAt) {
      setFormError("Pick a schedule date and time, or enable send immediately");
      return;
    }
    if (needsHeaderMedia && !headerMediaFile) {
      const label = getWhatsAppMediaTypeLabel(headerMediaType).toLowerCase();
      setFormError(`Upload a header ${label} for this template`);
      return;
    }
    if (needsHeaderTextVar && !String(headerTextVariable || "").trim()) {
      setFormError("Header variable is required for this template");
      return;
    }

    setSubmitting(true);
    try {
      let headerVariable = null;

      if (needsHeaderMedia && headerMediaFile) {
        const uploadRes = await uploadWhatsAppMedia(headerMediaFile);
        if (uploadRes?.success === false) {
          throw new Error(uploadRes.message || "Header media upload failed");
        }
        const mediaUrl =
          uploadRes?.url ||
          uploadRes?.data?.url ||
          uploadRes?.link ||
          null;
        if (!mediaUrl) {
          throw new Error(
            uploadRes?.message || "Header media upload did not return a URL",
          );
        }
        headerVariable = mediaUrl;
      } else if (needsHeaderTextVar) {
        headerVariable = String(headerTextVariable || "").trim() || null;
      }

      const payload = {
        title: title.trim(),
        template_name: getCampaignTemplateName(selectedTemplate),
        template_language: getCampaignTemplateLanguage(selectedTemplate),
        phonebook_id: Number(phonebookId),
        body_variables: variableIndexes.map((key) =>
          String(variableValues[key] || "").trim(),
        ),
        // Campaign API: header media URL or text placeholder value
        header_variable: headerVariable,
        button_variables: [],
        schedule_at:
          sendImmediately || !scheduleAt
            ? null
            : new Date(scheduleAt).toISOString(),
        timezone: TIMEZONE,
      };

      const res = await createCampaign(payload);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to send campaign");
      }
      toast.success("Campaign created");
      const id = res?.data?.id ?? res?.id;
      if (id != null) {
        navigate(`/whatsapp/campaigns/${id}`);
      } else {
        setStep(0);
        resetConfigureForm();
      }
    } catch (err) {
      const msg = reportError(err, "Failed to send campaign");
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WhatsAppPageShell className="whatsapp-page-shell--panel">
      <div className="whatsapp-inbox-root">
        <div className="whatsapp-page-panel">
          <div className="wa-campaign-page">
            <div className="wa-campaign-send-header">
              <Link to="/whatsapp/campaigns" className="whatsapp-back-link">
                <Icon icon="solar:arrow-left-line-duotone" width={18} />
                Back to My Campaign
              </Link>
              <h1>Send Campaign</h1>
            </div>

            <div className="wa-tpl-stepper wa-campaign-stepper">
              {CAMPAIGN_WIZARD_STEPS.map((item, idx) => {
                const done = idx < step;
                const active = idx === step;
                return (
                  <div
                    key={item.key}
                    className={`wa-tpl-step ${done ? "done" : ""} ${active ? "active" : ""}`}
                  >
                    <div className="wa-tpl-step-circle">
                      {done ? (
                        <Icon icon="solar:check-read-linear" width={16} />
                      ) : (
                        idx + 1
                      )}
                    </div>
                    <span>{item.label}</span>
                  </div>
                );
              })}
            </div>

            {step === 0 ? (
              <>
                <div className="wa-page-toolbar">
                  <div className="wa-page-toolbar-search">
                    <input
                      type="search"
                      placeholder="Search templates..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                      aria-label="Search templates"
                    />
                    <button
                      type="button"
                      className="wa-page-toolbar-search-btn"
                      onClick={handleSearch}
                    >
                      Search
                    </button>
                  </div>
                  <div className="wa-page-toolbar-actions">
                    <label className="wa-page-toolbar-filter">
                      <Icon icon="solar:filter-line-duotone" width={16} />
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        aria-label="Filter by status"
                      >
                        {CAMPAIGN_TEMPLATE_STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value || "all"} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="wa-page-toolbar-btn-outline"
                      onClick={fetchTemplates}
                      disabled={loadingTemplates}
                    >
                      <Icon icon="solar:refresh-linear" width={16} />
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="wa-campaign-step-body">
                  {loadingTemplates ? (
                    <div className="wa-campaign-template-grid">
                      {[1, 2, 3, 4, 5, 6].map((k) => (
                        <div key={k} className="wa-campaign-tpl-card skeleton" />
                      ))}
                    </div>
                  ) : templatesError ? (
                    <div className="whatsapp-list-error">{templatesError}</div>
                  ) : templates.length === 0 ? (
                    <div className="whatsapp-empty-state">
                      <Icon
                        icon="solar:document-text-line-duotone"
                        width={56}
                        style={{ opacity: 0.45 }}
                      />
                      <p>No templates found. Try another status or search.</p>
                    </div>
                  ) : (
                    <div className="wa-campaign-template-grid">
                      {templates.map((tpl) => {
                        const key = `${getCampaignTemplateName(tpl)}-${getCampaignTemplateLanguage(tpl)}`;
                        const isSelected =
                          selectedTemplate &&
                          getCampaignTemplateName(selectedTemplate) ===
                            getCampaignTemplateName(tpl) &&
                          getCampaignTemplateLanguage(selectedTemplate) ===
                            getCampaignTemplateLanguage(tpl);
                        return (
                          <TemplatePickerCard
                            key={key}
                            template={tpl}
                            selected={isSelected}
                            onSelect={selectTemplate}
                            onUse={useTemplate}
                            showUseButton
                          />
                        );
                      })}
                    </div>
                  )}
                </div>

                {templates.length > 0 ? (
                  <footer className="wa-campaign-wizard-footer wa-campaign-wizard-footer--pagination">
                    <div className="wa-campaigns-list-page-nav">
                      <button
                        type="button"
                        disabled={page <= 1 || loadingTemplates}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        aria-label="Previous page"
                      >
                        <Icon icon="solar:alt-arrow-left-linear" width={16} />
                      </button>
                      <span>
                        Page {page}
                        {totalPages > 1 ? ` of ${totalPages}` : ""}
                      </span>
                      <button
                        type="button"
                        disabled={!hasNextPage || loadingTemplates}
                        onClick={() => setPage((p) => p + 1)}
                        aria-label="Next page"
                      >
                        <Icon icon="solar:alt-arrow-right-linear" width={16} />
                      </button>
                    </div>
                  </footer>
                ) : null}
              </>
            ) : (
              <div className="wa-campaign-step-body wa-campaign-step-body--configure">
                <div className="wa-campaign-form-card">
                  <button
                    type="button"
                    className="wa-campaign-back-link"
                    onClick={goBack}
                  >
                    <Icon icon="solar:alt-arrow-left-linear" width={16} />
                    Back
                  </button>

                  {selectedTemplate ? (
                    <div className="wa-campaign-block">
                      <h3 className="wa-campaign-tpl-name">
                        {getCampaignTemplateName(selectedTemplate)}
                      </h3>
                      <p className="wa-campaign-tpl-meta">
                        Category:{" "}
                        {getCampaignTemplateCategory(selectedTemplate)}
                        {" | "}
                        Language:{" "}
                        {getCampaignTemplateLanguage(selectedTemplate)}
                        {" | "}
                        Status: {getCampaignTemplateStatus(selectedTemplate)}
                      </p>

                      <div className="wa-campaign-label">Template Content</div>
                      <div className="wa-campaign-template-preview-box">
                        {!needsHeaderMedia && headerText ? (
                          <div className="wa-campaign-preview-header">
                            {headerText}
                          </div>
                        ) : null}
                        <div className="wa-campaign-preview-body">
                          {bodyText || "No body text"}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="wa-campaign-block">
                    <label className="wa-campaign-field">
                      <span>Campaign Title</span>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Summer Sale 2025"
                      />
                    </label>
                  </div>

                  <div className="wa-campaign-block">
                    <h4 className="wa-campaign-block-title">Campaign Settings</h4>

                    <div className="wa-campaign-schedule-row wa-campaign-schedule-row--inline">
                      <label className="wa-campaign-toggle">
                        <input
                          type="checkbox"
                          checked={sendImmediately}
                          onChange={(e) => setSendImmediately(e.target.checked)}
                        />
                        <span className="wa-campaign-toggle-track" />
                        <span>No schedule (run immediately)</span>
                      </label>
                    </div>

                    {!sendImmediately ? (
                      <label className="wa-campaign-field">
                        <span>Schedule at</span>
                        <input
                          type="datetime-local"
                          value={scheduleAt}
                          onChange={(e) => setScheduleAt(e.target.value)}
                        />
                        <small>Timezone: {TIMEZONE}</small>
                      </label>
                    ) : null}

                    <label className="wa-campaign-field">
                      <span>Select phonebook *</span>
                      <select
                        value={phonebookId}
                        onChange={(e) => setPhonebookId(e.target.value)}
                        disabled={loadingPhonebooks}
                      >
                        <option value="">Select phonebook</option>
                        {phonebooks.map((pb) => (
                          <option key={pb.id} value={pb.id}>
                            {pb.name}
                            {pb.contactCount != null
                              ? ` (${pb.contactCount} Contacts)`
                              : ""}
                          </option>
                        ))}
                      </select>
                      <small>Broadcast will be sent to all contacts in this phonebook.</small>
                    </label>
                  </div>

                  {needsHeaderMedia || needsHeaderTextVar ? (
                    <div className="wa-campaign-block">
                      <h4 className="wa-campaign-block-title">
                        {needsHeaderMedia
                          ? `Header Media (${String(headerFormat || "").toUpperCase()})`
                          : "Header variable"}
                      </h4>

                      {needsHeaderMedia ? (
                        <div className="wa-campaign-header-media">
                          {headerMediaFile ? (
                            <div className="wa-campaign-header-media-selected">
                              <p className="wa-campaign-header-media-filename">
                                Selected media:{" "}
                                <span>{headerMediaFile.name}</span>
                              </p>
                              <div className="wa-campaign-header-media-frame">
                                {headerMediaType === "image" &&
                                headerMediaPreviewUrl ? (
                                  <img
                                    src={headerMediaPreviewUrl}
                                    alt={headerMediaFile.name}
                                    className="wa-campaign-header-media-preview"
                                  />
                                ) : headerMediaType === "video" &&
                                  headerMediaPreviewUrl ? (
                                  <video
                                    src={headerMediaPreviewUrl}
                                    className="wa-campaign-header-media-preview"
                                    controls
                                  />
                                ) : (
                                  <div className="wa-campaign-header-media-doc">
                                    <Icon
                                      icon="solar:document-linear"
                                      width={36}
                                    />
                                    <span>{headerMediaFile.name}</span>
                                  </div>
                                )}
                              </div>
                              <div className="wa-campaign-header-media-actions">
                                <button
                                  type="button"
                                  className="wa-campaign-header-btn wa-campaign-header-btn--remove"
                                  onClick={clearHeaderMedia}
                                  disabled={submitting}
                                >
                                  Remove
                                </button>
                                <button
                                  type="button"
                                  className="wa-campaign-header-btn wa-campaign-header-btn--change"
                                  onClick={() =>
                                    headerMediaInputRef.current?.click()
                                  }
                                  disabled={submitting}
                                >
                                  Change Media
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="wa-campaign-header-dropzone"
                              onClick={() =>
                                headerMediaInputRef.current?.click()
                              }
                              disabled={submitting}
                            >
                              <Icon
                                icon="solar:cloud-upload-linear"
                                width={28}
                              />
                              <strong>
                                Upload{" "}
                                {getWhatsAppMediaTypeLabel(headerMediaType)}
                              </strong>
                              <span>
                                {headerMediaType === "image"
                                  ? "JPEG, PNG, or WebP · max 16MB"
                                  : headerMediaType === "video"
                                    ? "MP4 or 3GPP · max 16MB"
                                    : "PDF or document · max 16MB"}
                              </span>
                            </button>
                          )}
                          <input
                            ref={headerMediaInputRef}
                            type="file"
                            className="wa-campaign-header-media-input"
                            accept={
                              WHATSAPP_MEDIA_ACCEPT[headerMediaType] ||
                              "image/jpeg,image/png,image/webp"
                            }
                            onChange={handleHeaderMediaChange}
                            disabled={submitting}
                          />
                        </div>
                      ) : null}

                      {needsHeaderTextVar ? (
                        <label className="wa-campaign-field">
                          <span>Header variable *</span>
                          <input
                            type="text"
                            value={headerTextVariable}
                            onChange={(e) =>
                              setHeaderTextVariable(e.target.value)
                            }
                            placeholder="Value for header {{1}}"
                            disabled={submitting}
                          />
                        </label>
                      ) : null}
                    </div>
                  ) : null}

                  {variableIndexes.length > 0 ? (
                    <div className="wa-campaign-block">
                      <h4 className="wa-campaign-block-title">Body variables</h4>
                      <div className="wa-campaign-vars-grid">
                        {variableIndexes.map((varKey) => (
                          <label key={varKey} className="wa-campaign-field">
                            <span>{`Variable {{${varKey}}}`}</span>
                            <input
                              type="text"
                              value={variableValues[varKey] || ""}
                              onChange={(e) =>
                                setVariableValues((prev) => ({
                                  ...prev,
                                  [varKey]: e.target.value,
                                }))
                              }
                              placeholder={`Value for {{${varKey}}}`}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {formError ? (
                    <div className="wa-tpl-step-error">{formError}</div>
                  ) : null}

                  <div className="wa-campaign-form-footer">
                    <button
                      type="button"
                      className="wa-tpl-btn-primary wa-campaign-send-btn"
                      disabled={!canSend}
                      onClick={handleSubmit}
                    >
                      <Icon icon="solar:plain-2-bold" width={16} />
                      {submitting
                        ? needsHeaderMedia
                          ? "Uploading..."
                          : "Sending..."
                        : "Send Message"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </WhatsAppPageShell>
  );
}
